import { create } from 'zustand';
import * as THREE from 'three';
import { getLayout } from '../logic/layouts';

// --- Data structure for a single particle in the system ---
export interface Particle {
  id: number;
  // Target values determined by the layout engine
  targetPosition: THREE.Vector3;
  targetOrientation: THREE.Quaternion;
  targetScale: THREE.Vector3;
  // Current values for animation
  currentPosition: THREE.Vector3;
  currentOrientation: THREE.Quaternion;
  currentScale: THREE.Vector3;
}

export type LayoutMode = 'fixed_particle_size' | 'solid_shape'; // Adjusted to new layout names
export type LayoutShape = 'sphere' | 'torus' | 'cylinder'; // Removed 'plane' as it's not in the new layouts

// --- The main state for the particle system ---
interface ParticleSystemState {
  // --- Core System Configuration ---
  particles: Particle[];
  particleCount: number;
  layoutMode: LayoutMode;
  layoutShape: LayoutShape;
  
  // --- Layout Parameters ---
  // Mode 1: Fixed Particle Size
  baseSizeX: number;
  baseSizeY: number;
  // Mode 2: Fixed Sphere Size
  targetRadius: number;
  
  // --- Shared Parameters ---
  baseDepth: number;
  baseTrapezoidY: number;
  baseTrapezoidZ: number;

  // --- Rendering ---
  sourceColor: string;
  metalness: number;
  roughness: number;

  // --- Actions ---
  setParticleCount: (count: number) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  setLayoutShape: (shape: LayoutShape) => void;
  setTargetRadius: (radius: number) => void;
  setBaseSizeX: (size: number) => void;
  setBaseSizeY: (size: number) => void;
  setBaseDepth: (size: number) => void;
  setBaseTrapezoidY: (value: number) => void;
  setBaseTrapezoidZ: (value: number) => void;
  setSourceColor: (color: string) => void;
  setMetalness: (value: number) => void;
  setRoughness: (value: number) => void;
  init: () => void;
}

const INITIAL_PARTICLE_COUNT = 100;

export const useParticleSystemStore = create<ParticleSystemState>((set, get) => ({
  // --- Initial State ---
  particles: [],
  particleCount: INITIAL_PARTICLE_COUNT,
  layoutMode: 'fixed_particle_size',
  layoutShape: 'sphere',

  baseSizeX: 0.2,
  baseSizeY: 0.2,
  targetRadius: 3,

  baseDepth: 0.2,
  baseTrapezoidY: 0.0,
  baseTrapezoidZ: 0.0,
  
  sourceColor: '#00ffaa',
  metalness: 0.5,
  roughness: 0.5,

  // --- Actions ---
  setParticleCount: (count) => {
    set({ particleCount: count });
    recalculateLayout(get, set);
  },
  setLayoutMode: (mode) => {
    set({ layoutMode: mode });
    recalculateLayout(get, set);
  },
  setLayoutShape: (shape) => {
    set({ layoutShape: shape });
    recalculateLayout(get, set);
  },
  setTargetRadius: (radius) => {
    set({ targetRadius: radius });
    recalculateLayout(get, set);
  },
  setBaseSizeX: (size) => {
    set({ baseSizeX: size });
    recalculateLayout(get, set);
  },
  setBaseSizeY: (size) => {
    set({ baseSizeY: size });
    recalculateLayout(get, set);
  },
  setBaseDepth: (depth) => {
    set({ baseDepth: depth });
    // Note: Depth change doesn't require re-layout, only re-geometry.
    // The canvas component will handle this.
    set({baseDepth: depth});
  },
  setBaseTrapezoidY: (value) => set({ baseTrapezoidY: value }),
  setBaseTrapezoidZ: (value) => set({ baseTrapezoidZ: value }),
  setSourceColor: (color) => set({ sourceColor: color }),
  setMetalness: (value) => set({ metalness: value }),
  setRoughness: (value) => set({ roughness: value }),
  init: () => {
    // This will be called from the canvas component to ensure layout is ready
    recalculateLayout(get, set);
  }
}));

// --- The Core Layout Engine ---
// This function is the heart of the system. It's called whenever a parameter changes.
function recalculateLayout(
  get: () => ParticleSystemState,
  set: (partial: Partial<ParticleSystemState>) => void
) {
  const { 
    particleCount,
    layoutMode,
    layoutShape,
    baseSizeX, // This is now particleSize for 'fixed_particle_size' mode
    targetRadius, // This is now shapeSize
    particles: currentParticles,
  } = get();

  // 1. Generate the target transforms based on the current state
  const layout = getLayout({
    count: particleCount,
    shape: layoutShape,
    mode: layoutMode,
    particleSize: baseSizeX, // Use baseSizeX as the single particleSize metric
    shapeSize: targetRadius,
  });

  // 2. Create or update the particle array
  const newParticles: Particle[] = [];

  for (let i = 0; i < particleCount; i++) {
    const oldParticle = currentParticles[i];
    const newPosition = layout.positions[i];
    const newScaleValue = layout.scales[i];

    if (!newPosition) continue; // Skip if layout doesn't provide a position

    const newScale = new THREE.Vector3(newScaleValue, newScaleValue, newScaleValue);

    if (oldParticle) {
      // Particle exists, update its target
      newParticles.push({
        ...oldParticle,
        targetPosition: newPosition,
        targetOrientation: new THREE.Quaternion(), // new layout doesn't provide orientation
        targetScale: newScale,
      });
    } else {
      // New particle, create it at the target location
      newParticles.push({
        id: i,
        targetPosition: newPosition,
        targetOrientation: new THREE.Quaternion(), // new layout doesn't provide orientation
        targetScale: newScale,
        // Initialize current values at the target to prevent initial animation
        currentPosition: newPosition.clone(),
        currentOrientation: new THREE.Quaternion(),
        currentScale: newScale.clone(),
      });
    }
  }

  set({ particles: newParticles });
}

// REMOVED: Initial calculation on load is no longer reliable here.
// recalculateLayout(useParticleSystemStore.getState, useParticleSystemStore.setState); 